import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Injector, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { createCustomElement } from '@angular/elements';
import { OverlayContainer } from '@angular/cdk/overlay';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { LicensesModule } from './components/licenses/licenses.module'; // Import LicensesModule
import { CardModule } from './components/card/card.module'; // Import CardModule
import { BuildingIntegrationModule } from './components/building-integration/building-integration.module'; // Import BuildingIntegrationModule
import { LicensesComponent } from './components/licenses/licenses.component';
import { ButtonModule, DialogModule, DropdownModule, SpinnerModule, FormFieldModule, HelperTextModule, InputModule, LabelModule, MenuModule, TableModule, TooltipModule, SnackbarMessageService, RestExceptionHandler } from '@ngx-interact-ui/core';
import { TranslateStore } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { environment } from './environments/environment';
import { CertificatesModule } from './components/certificates/certificates.module';
import { BuildingIntegrationComponent } from './components/building-integration/building-integration.component';

export function HttpLoaderFactory(http: HttpClient) {
  const baseUrl = `${environment.webcomponent_endpoint}/${environment.i18nPrefix}`;
  return new TranslateHttpLoader(http, baseUrl, '.json');
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    FormFieldModule,
    HelperTextModule,
    InputModule,
    LabelModule,
    MenuModule,
    SpinnerModule,
    TableModule,
    TooltipModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      }
    }),
    LicensesModule,
    CardModule,
    BuildingIntegrationModule,
    CertificatesModule
  ],
  providers: [TranslateService, TranslateStore, SnackbarMessageService, RestExceptionHandler],
  // bootstrap: [AppComponent], not needed as we are using custom elements
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(
    private injector: Injector,
    private overlayContainer: OverlayContainer
  ) {
    // Check if custom elements are already defined to prevent re-definition
    if (!customElements.get('wc-building-integration')) {
      const el = createCustomElement(BuildingIntegrationComponent, { injector: this.injector });
      customElements.define('wc-building-integration', el);
    }

    if (!customElements.get('wc-licenses')) {
      const e11 = createCustomElement(LicensesComponent, { injector: this.injector });
      customElements.define('wc-licenses', e11);
    }

    const container = this.overlayContainer.getContainerElement();

    this.copyAngularStylesToHost();
  }

  ngDoBootstrap(): void {
    // nothing here — bootstrap is handled by custom elements, not a root component
  }

  private copyAngularStylesToHost() {
    // Check if styles have already been copied to prevent duplication
    if (document.head.querySelector('[data-wc-styles-injected]')) {
      return;
    }

    try {
      // Get the host document (parent window if in iframe)
      const hostDocument = window.parent !== window ? window.parent.document : document;
      const hostHead = hostDocument.head;

      // Mark that styles are being injected
      const marker = hostDocument.createElement('meta');
      marker.setAttribute('data-wc-styles-injected', 'true');
      hostHead.appendChild(marker);

      // Copy all style elements
      const styleElements = document.querySelectorAll('style');
      styleElements.forEach(styleEl => {
        if (!styleEl.hasAttribute('data-wc-style')) {
          const clonedStyle = styleEl.cloneNode(true) as HTMLStyleElement;
          clonedStyle.setAttribute('data-wc-style', 'true');
          hostHead.appendChild(clonedStyle);
        }
      });

      // Copy all stylesheet link elements
      const linkElements = document.querySelectorAll('link[rel="stylesheet"]');
      linkElements.forEach(linkEl => {
        if (!linkEl.hasAttribute('data-wc-link')) {
          const clonedLink = linkEl.cloneNode(true) as HTMLLinkElement;
          clonedLink.setAttribute('data-wc-link', 'true');
          hostHead.appendChild(clonedLink);
        }
      });

      // Extract and inject all CSS rules from stylesheets
      this.injectInteractUIStyles(hostDocument);

    } catch (error) {
      console.warn('Error copying styles to host:', error);
    }
  }

  private injectInteractUIStyles(hostDocument: Document) {
    try {
      const hostHead = hostDocument.head;

      // Check if comprehensive styles have already been injected
      if (hostHead.querySelector('[data-wc-comprehensive-styles]')) {
        return;
      }

      const allCSSRules: string[] = [];

      // Extract CSS rules from all stylesheets
      Array.from(document.styleSheets).forEach(stylesheet => {
        try {
          if (stylesheet.cssRules) {
            Array.from(stylesheet.cssRules).forEach(rule => {
              allCSSRules.push(rule.cssText);
            });
          }
        } catch (e) {
          // Handle CORS issues with external stylesheets
          console.warn('Cannot access stylesheet rules (likely CORS):', stylesheet.href);
        }
      });

      // Create a comprehensive style element with all CSS rules
      if (allCSSRules.length > 0) {
        const comprehensiveStyle = hostDocument.createElement('style');
        comprehensiveStyle.setAttribute('data-wc-comprehensive-styles', 'true');
        comprehensiveStyle.textContent = allCSSRules.join('\n');
        hostHead.appendChild(comprehensiveStyle);
      }

    } catch (error) {
      console.warn('Error injecting comprehensive styles:', error);
    }
  }
}
